<script setup lang="ts">
import { watchEffect } from 'vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BHotkey from './components/base/BHotkey/BHotkey.vue'
import SHeader from './components/singleton/SHeader/SHeader.vue'
import SSnackbar from './components/singleton/SSnackbar/SSnackbar.vue'
import SAgreementsToSignDialog from './components/singleton/SAgreementsToSignDialog/SAgreementsToSignDialog.vue'
import SCreditorOnboardingCompletedDialog from './components/singleton/SCreditorOnboardingCompletedDialog/SCreditorOnboardingCompletedDialog.vue'
import SRouteGuardDialog from './components/singleton/SRouteGuardDialog/SRouteGuardDialog.vue'
import { useQueryClient } from '@tanstack/vue-query'
import useAuthStore, { isUserToken } from './stores/authStore'
import { useUserExtendedData } from './composables/useUserExtendedData'
import { useAuthEffects } from './composables/useAuthEffects'
import { useAuthTabSync } from './composables/useAuthTabSync'
import { setDatadogUserAndAccount } from './plugins/datadog'
import { identifySurvicateUser, clearSurvicateUser } from './plugins/survicate'
import { identifyUserGuidingUser } from './plugins/userguiding'

const { locale } = useI18n()
const route = useRoute()
const authStore = useAuthStore()
const userExtendedData = useUserExtendedData()

useAuthEffects()
useAuthTabSync()

useHead({
  htmlAttrs: {
    lang: locale,
  },
})

const queryClient = useQueryClient()

function refetchAllQueries() {
  console.log('refetching all active queries')
  void queryClient.invalidateQueries({ refetchType: 'active' })
}

// Identify user to external tools when user data is available
watchEffect(() => {
  const tokenPayload = authStore.selectedToken?.payload
  const userData = userExtendedData.data.value

  // Clear user data if not authenticated
  if (!isUserToken(tokenPayload)) {
    setDatadogUserAndAccount(null, null)
    clearSurvicateUser()
    return
  }

  const userId = tokenPayload.u_id
  const accountId = tokenPayload.a_id
  const isSolicitor = tokenPayload.solicitor === true
  const email = tokenPayload.u_email

  // DataDog: Identify user and account
  setDatadogUserAndAccount(
    {
      id: userId,
      name: tokenPayload.u_name,
      email,
      solicitor: isSolicitor,
    },
    accountId != null
      ? {
          id: accountId,
        }
      : null
  )

  // UserGuiding: Identify user (always)
  identifyUserGuidingUser(userId, {
    name: tokenPayload.u_name,
    email,
    solicitor: isSolicitor,
    accountId,
  })

  // Survicate: Identify user (only for users with email)
  if (userData != null && (email as string | null) != null) {
    identifySurvicateUser({
      user_id: userData._id,
      email,
      created_at: new Date(userData.created),
      solicitor: isSolicitor,
    })
  }
})
</script>

<template>
  <v-app>
    <b-hotkey
      keys="alt+shift+r"
      no-tooltip
      @trigger="refetchAllQueries"
    />
    <s-header />
    <v-main scrollable>
      <v-container
        v-if="route.meta.fullscreen !== true"
        :fluid="route.meta.fluid === true"
        class="min-h-100"
      >
        <router-view />
      </v-container>
      <router-view v-else />
      <s-snackbar />
      <s-agreements-to-sign-dialog />
      <s-route-guard-dialog />
      <s-creditor-onboarding-completed-dialog />
    </v-main>
  </v-app>
</template>

<style scoped>
.min-h-100 {
  min-height: 100%;
}
</style>
